import { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Trophy, Medal, TrendingUp, Target, Calendar, Clock, CheckCircle, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { usePrizes, Prize } from '@/hooks/usePrizes';
import { useAuth } from '@/contexts/AuthContext';
import { VolumeBonusProgress } from './VolumeBonusProgress';
import { PrizeTimelineCard } from './PrizeTimelineCard';
import { ClaimPrizeModal } from './ClaimPrizeModal';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format, addMonths, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';

export function PrizesScreen() {
  const { user } = useAuth();
  const {
    prizes,
    loading,
    currentRanking,
    userPosition,
    claimPrize,
    getClaimablePrize,
    formatMonthYear,
    getCurrentMonthYear,
    getUserVolumeBonuses,
    getUserVolumeSales,
    getAllClaimablePrizes,
  } = usePrizes(user?.id);

  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [showAllFuture, setShowAllFuture] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const claimablePrize = getClaimablePrize();
  const allClaimablePrizes = getAllClaimablePrizes();
  const volumeSales = getUserVolumeSales();
  const volumeBonuses = getUserVolumeBonuses();
  const currentMonthYear = getCurrentMonthYear();
  const currentLeader = currentRanking[0];

  // Categorize prizes
  const currentPrize = prizes.find(p => p.month_year === currentMonthYear);
  const futurePrizes = prizes.filter(p => p.month_year > currentMonthYear).sort((a, b) => a.month_year.localeCompare(b.month_year));
  const pastPrizes = prizes.filter(p => p.month_year < currentMonthYear).sort((a, b) => b.month_year.localeCompare(a.month_year));

  const handleOpenClaimModal = (prize: Prize) => {
    setSelectedPrize(prize);
    setClaimModalOpen(true);
  };

  const handleClaim = async () => {
    if (!selectedPrize) return { success: false, claimCode: null };
    return claimPrize(selectedPrize.id);
  };

  const getPositionMessage = () => {
    if (!userPosition) return null;
    
    if (userPosition === 1) {
      return {
        icon: Trophy,
        color: 'text-amber-400',
        bg: 'bg-amber-500/20',
        message: '¡Vas en primer lugar! Mantén el ritmo para ganar el premio 🔥',
      };
    }
    
    if (userPosition <= 3) {
      return {
        icon: Medal,
        color: 'text-zinc-300',
        bg: 'bg-zinc-500/20',
        message: `Estás en el #${userPosition}. ¡Sube ${userPosition - 1} posición${userPosition > 2 ? 'es' : ''} para ganar!`,
      };
    }
    
    return {
      icon: TrendingUp,
      color: 'text-blue-400',
      bg: 'bg-blue-500/20',
      message: `Estás en el #${userPosition}. ¡Sigue vendiendo para subir en el ranking!`,
    };
  };

  const getDaysRemaining = () => {
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return differenceInDays(endOfMonth, now);
  };

  const positionInfo = getPositionMessage();
  const daysRemaining = getDaysRemaining();

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-24 w-full bg-zinc-800" />
        <Skeleton className="h-40 w-full bg-zinc-800" />
        <Skeleton className="h-40 w-full bg-zinc-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
          className="inline-block mb-4"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full" />
            <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-full p-4 relative z-10">
              <Gift className="h-10 w-10 text-amber-400" />
            </div>
          </div>
        </motion.div>
        <h1 className="text-2xl font-bold text-white mb-1">Premios Mensuales</h1>
        <p className="text-zinc-400">Los mejores del ranking ganan premios en efectivo</p>
      </div>

      {/* Prize Structure */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 border border-zinc-700 rounded-xl overflow-hidden"
      >
        <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20">
          <h3 className="text-amber-300 font-semibold flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Premios por Ranking
          </h3>
        </div>
        <div className="p-4 grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
            <div className="text-2xl mb-1">🥇</div>
            <p className="text-xs text-zinc-400">1° Lugar</p>
            <p className="text-lg font-bold text-amber-400">₡40,000</p>
          </div>
          <div className="text-center p-3 bg-zinc-500/10 rounded-xl border border-zinc-500/30">
            <div className="text-2xl mb-1">🥈</div>
            <p className="text-xs text-zinc-400">2° Lugar</p>
            <p className="text-lg font-bold text-zinc-300">₡30,000</p>
          </div>
          <div className="text-center p-3 bg-orange-500/10 rounded-xl border border-orange-500/30">
            <div className="text-2xl mb-1">🥉</div>
            <p className="text-xs text-zinc-400">3° Lugar</p>
            <p className="text-lg font-bold text-orange-400">₡20,000</p>
          </div>
        </div>
      </motion.div>

      {/* Volume Bonus Progress */}
      <VolumeBonusProgress
        bronceSales={volumeSales.bronce}
        plataSales={volumeSales.plata}
        oroSales={volumeSales.oro}
        earnedBonuses={volumeBonuses}
        onClaimBonus={handleOpenClaimModal}
      />

      {/* User Position Card */}
      {positionInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${positionInfo.bg} border border-${positionInfo.color.replace('text-', '')}/30 rounded-xl p-4`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${positionInfo.bg}`}>
              <positionInfo.icon className={`h-6 w-6 ${positionInfo.color}`} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-white">
                Tu posición: #{userPosition} de {currentRanking.length}
              </p>
              <p className="text-sm text-zinc-400">{positionInfo.message}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Claimable Prize Alert */}
      {claimablePrize && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 rounded-xl p-5 cursor-pointer"
          onClick={() => handleOpenClaimModal(claimablePrize)}
        >
          <div className="flex items-center gap-4">
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, -5, 5, 0],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Gift className="h-12 w-12 text-green-400" />
            </motion.div>
            <div className="flex-1">
              <p className="text-green-400 font-bold text-lg">🎉 ¡Tienes un premio por reclamar!</p>
              <p className="text-white font-semibold">{claimablePrize.prize_name}</p>
              <p className="text-sm text-zinc-400 capitalize">{formatMonthYear(claimablePrize.month_year)}</p>
            </div>
            <div className="text-green-400 animate-pulse">
              <Target className="h-6 w-6" />
            </div>
          </div>
        </motion.div>
      )}

      {/* Current Month Prize */}
      {currentPrize && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-amber-500/15 to-orange-500/10 border border-amber-500/30 rounded-xl overflow-hidden"
        >
          <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" />
              <span className="text-amber-300 font-semibold">Premio en Curso</span>
            </div>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
              <Clock className="h-3 w-3 mr-1" />
              {daysRemaining} días restantes
            </Badge>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <h3 className="text-lg font-bold text-white">{currentPrize.prize_name}</h3>
              {currentPrize.prize_description && (
                <p className="text-sm text-zinc-400 mt-1">{currentPrize.prize_description}</p>
              )}
              {currentPrize.prize_value && (
                <p className="text-sm text-amber-400 mt-1">
                  Valor: ₡{currentPrize.prize_value.toLocaleString()}
                </p>
              )}
            </div>
            
            {currentLeader && (
              <div className="bg-black/20 rounded-lg p-3">
                <p className="text-xs text-zinc-500 mb-1">Líder actual</p>
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-amber-400" />
                  <span className="font-semibold text-white">{currentLeader.nombre}</span>
                  <span className="text-amber-400 text-sm">({currentLeader.puntuacion} pts)</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Future Prizes */}
      {futurePrizes.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-400" />
            Próximos Premios
          </h2>
          
          <div className="space-y-2">
            {(showAllFuture ? futurePrizes : futurePrizes.slice(0, 3)).map((prize, index) => (
              <motion.div
                key={prize.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <Gift className="h-4 w-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{prize.prize_name}</p>
                      <p className="text-sm text-zinc-500 capitalize">{formatMonthYear(prize.month_year)}</p>
                    </div>
                  </div>
                  {prize.prize_value && (
                    <span className="text-sm text-zinc-400">₡{prize.prize_value.toLocaleString()}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          
          {futurePrizes.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAllFuture(!showAllFuture)}
              className="w-full text-zinc-400 hover:text-white"
            >
              {showAllFuture ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Ver menos
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Ver {futurePrizes.length - 3} más
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Past Prizes (History) */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-400" />
          Historial de Premios
        </h2>

        {pastPrizes.length === 0 && !currentPrize ? (
          <div className="text-center py-12 text-zinc-500">
            <Gift className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No hay premios registrados aún</p>
            <p className="text-sm">El administrador configurará los premios mensuales</p>
          </div>
        ) : (
          <div className="space-y-3">
            {(showAllHistory ? pastPrizes : pastPrizes.slice(0, 5)).map((prize, index) => (
              <motion.div
                key={prize.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <PrizeTimelineCard
                  prize={prize}
                  isCurrentMonth={false}
                  isWinner={prize.winner_id === user?.id}
                  onClaim={
                    prize.winner_id === user?.id && prize.status === 'claimable'
                      ? () => handleOpenClaimModal(prize)
                      : undefined
                  }
                  formatMonthYear={formatMonthYear}
                />
              </motion.div>
            ))}
            
            {pastPrizes.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllHistory(!showAllHistory)}
                className="w-full text-zinc-400 hover:text-white"
              >
                {showAllHistory ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Ver menos
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Ver {pastPrizes.length - 5} más
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Claim Modal */}
      <ClaimPrizeModal
        open={claimModalOpen}
        onOpenChange={setClaimModalOpen}
        prize={selectedPrize}
        onClaim={handleClaim}
        formatMonthYear={formatMonthYear}
      />
    </div>
  );
}